import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate, useLocation } from 'react-router-dom';
import { authStore } from '@/stores/AuthStore';
import { AppCodeInput, AppButton, AppLogo } from '@pro-sto/ui';
import { ROUTES } from '@/constants/routes';
import './AuthCodePage.css';

interface LocationState {
  phone?: string;
}

const AuthCodePage: React.FC = observer(() => {
  const [code, setCode] = useState('');
  const [timer, setTimer] = useState(60);
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState;
  const phone = state?.phone;

  const canResend = timer === 0;

  useEffect(() => {
    if (!phone) {
      navigate(ROUTES.AUTH_PHONE, { replace: true });
    }
  }, [phone, navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleCodeComplete = async (completedCode: string) => {
    if (!phone) return;
    try {
      await authStore.verifyCode(phone, completedCode);
      navigate(ROUTES.DASHBOARD);
    } catch (error) {
      console.error('Error verifying code:', error);
      setCode('');
    }
  };

  const handleCodeChange = (newCode: string) => {
    setCode(newCode);
  };

  const handleResend = async () => {
    if (!canResend || !phone) return;
    try {
      await authStore.sendCode(phone);
      setTimer(60);
      setCode('');
    } catch (error) {
      console.error('Error resending code:', error);
    }
  };

  const handleSubmit = async () => {
    if (code.length !== 4 || !phone) return;
    await handleCodeComplete(code);
  };

  if (!phone) return null;

  const canResendActive = canResend && !authStore.isLoading;

  return (
    <div className="auth-code" data-testid="auth-code-page">
      <div className="auth-code__logo" data-testid="auth-logo">
        <AppLogo />
      </div>
      <h1 className="auth-code__title" data-testid="auth-code-title">Авторизация</h1>
      <div className="auth-code__form">
        <AppCodeInput
          value={code}
          label="Введите код из СМС"
          length={4}
          onChange={handleCodeChange}
          onComplete={handleCodeComplete}
          disabled={authStore.isLoading}
          autoFocus
          data-testid="code-input"
        />
        <button
          className={`auth-code__resend ${canResendActive ? 'auth-code__resend_active' : 'auth-code__resend_disabled'}`}
          onClick={handleResend}
          disabled={!canResend || authStore.isLoading}
          data-testid="resend-code-button"
        >
          {canResend ? 'Отправить повторно' : `Отправить повторно через ${timer}с`}
        </button>
        <div className="auth-code__button">
          <AppButton
            variant="primary"
            size="L"
            onClick={handleSubmit}
            disabled={code.length !== 4 || authStore.isLoading}
            loading={authStore.isLoading}
            data-testid="submit-code-button"
          >
            Далее
          </AppButton>
        </div>
      </div>
    </div>
  );
});

AuthCodePage.displayName = 'AuthCodePage';

export default AuthCodePage;
